import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Save, Trash2, Clock, Hash, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { participantsApi } from '@/api/endpoints/participants';
import { practicalScoresApi } from '@/api/endpoints/practicalScores';
import { PracticalScore } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { cn } from '@/utils/cn';

// Stable empty reference — avoids re-creating a new [] on every render which would cause
// practicalMap → participants → useEffect cascade and infinite re-render loop.
const EMPTY_SCORES: PracticalScore[] = [];

type TimeMode = 'minutes' | 'range';

interface RowState {
  score: string;
  timeMode: TimeMode;
  durationText: string; // tryb ręczny: czas w formacie mm:ss
  startTime: string;    // tryb zakres: HH:MM:SS
  endTime: string;
  dirty: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Total seconds → "mm:ss" (np. 803 → "13:23"). */
function formatSeconds(total: number | null): string {
  if (total === null) return '';
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${pad2(m)}:${pad2(s)}`;
}

/** Parse "mm:ss" / "m:ss" / "mm" → łączna liczba sekund. null jeśli niepoprawne. */
function parseMmSs(str: string): number | null {
  const t = str.trim();
  if (!/^\d{1,4}(:\d{1,2})?$/.test(t)) return null;
  const [mStr, sStr] = t.split(':');
  const m = Number(mStr);
  const s = sStr !== undefined ? Number(sStr) : 0;
  if (isNaN(m) || isNaN(s) || s >= 60) return null;
  return m * 60 + s;
}

/** Godzina zegarowa "HH:MM" lub "HH:MM:SS" → sekundy od północy. */
function timeToSeconds(t: string): number | null {
  if (!t) return null;
  const parts = t.split(':').map(Number);
  if (parts.some((n) => isNaN(n))) return null;
  const [h, m, s = 0] = parts;
  return h * 3600 + m * 60 + s;
}

/** Różnica między dwiema godzinami zegarowymi w sekundach. */
function calcSeconds(start: string, end: string): number | null {
  const s = timeToSeconds(start);
  const e = timeToSeconds(end);
  if (s === null || e === null) return null;
  const diff = e - s;
  return diff > 0 ? diff : null;
}

/** Godzina zegarowa → znormalizowany ISO 1970-01-01THH:MM:SS.000Z. */
function timeToIso(t: string): string {
  const [h = '0', m = '0', s = '0'] = t.split(':');
  return `1970-01-01T${pad2(Number(h))}:${pad2(Number(m))}:${pad2(Number(s))}.000Z`;
}

function makeDefaultRow(ps: PracticalScore | null): RowState {
  if (!ps) {
    return { score: '', timeMode: 'minutes', durationText: '', startTime: '', endTime: '', dirty: false };
  }
  const hasRange = ps.startTime !== null && ps.endTime !== null;
  const startTime = ps.startTime ? ps.startTime.slice(11, 19) : '';
  const endTime = ps.endTime ? ps.endTime.slice(11, 19) : '';
  return {
    score: String(ps.score),
    timeMode: hasRange ? 'range' : 'minutes',
    durationText: ps.durationSeconds !== null ? formatSeconds(ps.durationSeconds) : '',
    startTime,
    endTime,
    dirty: false,
  };
}

interface ParticipantRow {
  id: string;
  firstName: string;
  lastName: string;
  school: { id: string; name: string } | null;
  email: string;
  practicalScore: PracticalScore | null;
}

export const PracticalScoresPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [defaultStartTime, setDefaultStartTime] = useState<string>(
    () => localStorage.getItem('practicalScores.defaultStartTime') ?? '',
  );
  const initializedRef = useRef(false);

  const { data: stage2Data, isLoading: loadingParticipants } = useQuery({
    queryKey: ['participants-stage2'],
    queryFn: () => participantsApi.getAll({ limit: 2000, sessionStage: 2 }),
  });

  const { data: practicalScoresData, isLoading: loadingPractical } = useQuery({
    queryKey: ['practical-scores'],
    queryFn: () => practicalScoresApi.getAll(),
  });
  const practicalScores = practicalScoresData ?? EMPTY_SCORES;

  const practicalMap = useMemo(
    () => new Map(practicalScores.map((ps) => [ps.studentId, ps])),
    [practicalScores],
  );

  const participants: ParticipantRow[] = useMemo(() => {
    if (!stage2Data) return [];
    return stage2Data.data
      .map((p) => ({
        id: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        school: p.school ?? null,
        email: p.email,
        practicalScore: practicalMap.get(p.id) ?? null,
      }))
      .sort((a, b) => a.lastName.localeCompare(b.lastName, 'pl'));
  }, [stage2Data, practicalMap]);

  // Initialise row states once data arrives — don't overwrite dirty rows
  useEffect(() => {
    if (participants.length === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    const initial: Record<string, RowState> = {};
    for (const p of participants) {
      initial[p.id] = makeDefaultRow(p.practicalScore);
    }
    setRows(initial);
  }, [participants]);

  // When practical scores refresh from server, reset non-dirty rows
  useEffect(() => {
    if (participants.length === 0) return;
    setRows((prev) => {
      const next = { ...prev };
      for (const p of participants) {
        if (!next[p.id] || !next[p.id].dirty) {
          next[p.id] = makeDefaultRow(p.practicalScore);
        }
      }
      return next;
    });
  }, [practicalScores, participants]);

  const upsertMutation = useMutation({
    mutationFn: ({ studentId, row }: { studentId: string; row: RowState }) => {
      const score = Number(row.score);
      let durationSeconds: number | null = null;
      let startTime: string | null = null;
      let endTime: string | null = null;

      if (row.timeMode === 'minutes' && row.durationText.trim() !== '') {
        durationSeconds = parseMmSs(row.durationText);
      } else if (row.timeMode === 'range') {
        durationSeconds = calcSeconds(row.startTime, row.endTime);
        if (row.startTime) startTime = timeToIso(row.startTime);
        if (row.endTime) endTime = timeToIso(row.endTime);
      }

      return practicalScoresApi.upsert(studentId, { score, durationSeconds, startTime, endTime });
    },
    onSuccess: (_, { studentId }) => {
      setRows((prev) => ({ ...prev, [studentId]: { ...prev[studentId], dirty: false } }));
      queryClient.invalidateQueries({ queryKey: ['practical-scores'] });
      queryClient.invalidateQueries({ queryKey: ['stage-completers', 'stage2'] });
      toast.success('Wynik zapisany');
    },
    onError: () => toast.error('Nie udało się zapisać wyniku'),
  });

  const deleteMutation = useMutation({
    mutationFn: (studentId: string) => practicalScoresApi.remove(studentId),
    onSuccess: (_, studentId) => {
      setRows((prev) => ({ ...prev, [studentId]: makeDefaultRow(null) }));
      queryClient.invalidateQueries({ queryKey: ['practical-scores'] });
      queryClient.invalidateQueries({ queryKey: ['stage-completers', 'stage2'] });
      toast.success('Wynik usunięty');
    },
    onError: () => toast.error('Nie udało się usunąć wyniku'),
  });

  const updateRow = (id: string, patch: Partial<RowState>) => {
    clearError(id);
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch, dirty: true } }));
  };

  const handleDefaultStartTimeChange = (value: string) => {
    setDefaultStartTime(value);
    if (value) {
      localStorage.setItem('practicalScores.defaultStartTime', value);
    } else {
      localStorage.removeItem('practicalScores.defaultStartTime');
    }
    if (!value) return;
    setRows((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (!next[id].startTime) {
          next[id] = { ...next[id], startTime: value };
        }
      }
      return next;
    });
  };

  const setError = (id: string, msg: string) =>
    setRowErrors((prev) => ({ ...prev, [id]: msg }));
  const clearError = (id: string) =>
    setRowErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });

  const handleSave = (p: ParticipantRow) => {
    const row = rows[p.id];
    if (!row) return;
    const score = Number(row.score);
    if (row.score === '' || isNaN(score) || score < 0) {
      setError(p.id, 'Wpisz prawidłową liczbę punktów.');
      return;
    }
    if (row.timeMode === 'minutes') {
      const secs = parseMmSs(row.durationText);
      if (row.durationText.trim() === '' || secs === null || secs <= 0) {
        setError(p.id, 'Czas wykonania jest wymagany — wpisz czas w formacie mm:ss.');
        return;
      }
    }
    if (row.timeMode === 'range') {
      if (!row.startTime || !row.endTime) {
        setError(p.id, 'Czas wykonania jest wymagany — podaj godzinę startu i końca.');
        return;
      }
      const calc = calcSeconds(row.startTime, row.endTime);
      if (calc === null) {
        setError(p.id, 'Godzina zakończenia musi być późniejsza niż godzina startu.');
        return;
      }
    }
    clearError(p.id);
    upsertMutation.mutate({ studentId: p.id, row });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter((p) => {
      const name = `${p.firstName} ${p.lastName}`.toLowerCase();
      return name.includes(q) || p.email.toLowerCase().includes(q) || (p.school?.name.toLowerCase().includes(q) ?? false);
    });
  }, [participants, search]);

  const isLoading = loadingParticipants || loadingPractical;

  const savedCount = practicalScores.length;
  const totalCount = participants.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Wyniki praktyczne — Etap 2</h2>
          <p className="mt-1 text-sm text-gray-500">
            Ręczne wprowadzanie punktów z części praktycznej dla uczestników Etapu 2
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="text-2xl font-bold text-gray-800">
            <span className="text-primary-600">{savedCount}</span>
            <span className="text-gray-400 font-normal"> / {totalCount}</span>
          </span>
          <span className="text-xs text-gray-400 -mt-1">wprowadzonych wyników</span>
          <div className="h-2 w-48 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-500"
              style={{ width: `${(savedCount / Math.max(totalCount, 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        {/* Tryby — lewa strona */}
        <div className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Tryby wprowadzania czasu — wybierz dla każdego uczestnika:
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-100">
                <Hash className="h-3.5 w-3.5 text-blue-600" />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-700">Tryb ręczny</p>
                <p className="text-xs text-gray-500">Wpisz łączny czas w formacie mm:ss (np. 13:30)</p>
              </div>
            </div>
            <div className="hidden sm:block w-px bg-gray-200 self-stretch" />
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet-100">
                <Clock className="h-3.5 w-3.5 text-violet-600" />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-700">Tryb zakres godzinowy</p>
                <p className="text-xs text-gray-500">Podaj godzinę startu i końca — czas obliczany automatycznie</p>
              </div>
            </div>
          </div>
        </div>

        {/* Domyślna godzina startu — prawa strona */}
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 sm:w-72">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-700">
            Domyślna godzina startu
          </p>
          <p className="mb-3 text-xs text-amber-600">
            Uzupełnia automatycznie pole „Start" dla wszystkich uczestników. Zapisywana po odświeżeniu.
          </p>
          <Input
            type="time"
            value={defaultStartTime}
            onChange={(e) => handleDefaultStartTimeChange(e.target.value)}
            className="w-36 text-center font-mono text-base font-semibold"
          />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <CardTitle className="flex-1">Uczestnicy Etapu 2</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Szukaj uczestnika..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-10" />
          ) : participants.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              Brak zakwalifikowanych uczestników do Etapu 2
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8 text-center">#</TableHead>
                  <TableHead>Uczestnik</TableHead>
                  <TableHead>Szkoła</TableHead>
                  <TableHead className="w-32 text-center">Punkty prakt.</TableHead>
                  <TableHead className="w-48 text-center">Czas wykonania</TableHead>
                  <TableHead className="w-36 text-center">Tryb czasu</TableHead>
                  <TableHead className="w-40 text-center">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p, idx) => {
                  const row = rows[p.id];
                  const hasSaved = !!p.practicalScore;
                  const isSaving =
                    upsertMutation.isPending && upsertMutation.variables?.studentId === p.id;
                  const isDeleting =
                    deleteMutation.isPending && deleteMutation.variables === p.id;
                  const calcSec = row?.timeMode === 'range' ? calcSeconds(row.startTime, row.endTime) : null;

                  return (
                    <React.Fragment key={p.id}>
                    <TableRow
                      className={cn(
                        hasSaved && !row?.dirty ? 'bg-green-50/60' : undefined,
                        row?.dirty ? 'bg-amber-50/50' : undefined,
                      )}
                    >
                      {/* # */}
                      <TableCell className="text-center text-gray-400 text-xs font-mono">
                        {idx + 1}
                      </TableCell>

                      {/* Uczestnik */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {hasSaved && !row?.dirty && (
                            <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                          )}
                          <div>
                            <div className="font-medium text-gray-900">
                              {p.firstName} {p.lastName}
                            </div>
                            <div className="text-xs text-gray-400">{p.email}</div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Szkoła */}
                      <TableCell>
                        {p.school ? (
                          <span className="text-xs bg-gray-100 rounded px-2 py-0.5 text-gray-700">
                            {p.school.name}
                          </span>
                        ) : '—'}
                      </TableCell>

                      {/* Punkty */}
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min={0}
                          step={0.5}
                          value={row?.score ?? ''}
                          onChange={(e) => updateRow(p.id, { score: e.target.value })}
                          placeholder="0"
                          className="w-24 text-center mx-auto font-mono placeholder:text-gray-200"
                        />
                      </TableCell>

                      {/* Czas */}
                      <TableCell className="text-center">
                        {row?.timeMode === 'minutes' ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={row?.durationText ?? ''}
                              onChange={(e) => updateRow(p.id, { durationText: e.target.value })}
                              placeholder="mm:ss"
                              className={cn('w-24 text-center font-mono', rowErrors[p.id] && 'border-red-500 ring-1 ring-red-500')}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 justify-center flex-wrap">
                            <Input
                              type="time"
                              step={1}
                              value={row?.startTime ?? ''}
                              onChange={(e) => updateRow(p.id, { startTime: e.target.value })}
                              className={cn('w-[6.5rem] text-center font-mono text-xs px-1', rowErrors[p.id] && !row?.startTime && 'border-red-500 ring-1 ring-red-500')}
                            />
                            <span className="text-gray-400 text-xs">—</span>
                            <Input
                              type="time"
                              step={1}
                              value={row?.endTime ?? ''}
                              onChange={(e) => updateRow(p.id, { endTime: e.target.value })}
                              className={cn('w-[6.5rem] text-center font-mono text-xs px-1', rowErrors[p.id] && !row?.endTime && 'border-red-500 ring-1 ring-red-500')}
                            />
                            {calcSec !== null && (
                              <span className="text-xs text-violet-600 font-mono whitespace-nowrap">
                                = {formatSeconds(calcSec)}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>

                      {/* Tryb czasu */}
                      <TableCell className="text-center">
                        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                          <button
                            onClick={() => updateRow(p.id, { timeMode: 'minutes' })}
                            className={cn(
                              'flex items-center gap-1 px-2.5 py-1.5 transition-colors',
                              row?.timeMode === 'minutes'
                                ? 'bg-blue-500 text-white'
                                : 'bg-white text-gray-500 hover:bg-gray-50',
                            )}
                          >
                            <Hash className="h-3 w-3" />
                            mm:ss
                          </button>
                          <button
                            onClick={() => updateRow(p.id, {
                              timeMode: 'range',
                              ...(defaultStartTime && !row?.startTime ? { startTime: defaultStartTime } : {}),
                            })}
                            className={cn(
                              'flex items-center gap-1 px-2.5 py-1.5 transition-colors border-l border-gray-200',
                              row?.timeMode === 'range'
                                ? 'bg-violet-500 text-white'
                                : 'bg-white text-gray-500 hover:bg-gray-50',
                            )}
                          >
                            <Clock className="h-3 w-3" />
                            Start/Stop
                          </button>
                        </div>
                      </TableCell>

                      {/* Akcje */}
                      <TableCell className="text-center">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleSave(p)}
                            disabled={isSaving || !row?.dirty}
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40',
                              row?.dirty
                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                : 'bg-gray-100 text-gray-500 cursor-default',
                            )}
                            title="Zapisz wynik"
                          >
                            {isSaving ? (
                              <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                            Zapisz
                          </button>
                          {hasSaved && (
                            <button
                              onClick={() => deleteMutation.mutate(p.id)}
                              disabled={isDeleting}
                              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-40"
                              title="Usuń wynik"
                            >
                              {isDeleting ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {rowErrors[p.id] && (
                      <TableRow className="bg-red-50 border-0">
                        <TableCell colSpan={6} className="py-1.5 px-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">!</span>
                            {rowErrors[p.id]}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          {filtered.length} uczestnik{filtered.length === 1 ? '' : filtered.length < 5 ? 'ów' : 'ów'} — Etap 2 część praktyczna
        </p>
      )}
    </div>
  );
};
